import { useEffect, useState } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
  CollectionPreferences,
} from '@amzn/awsui-components-console';
import {
  ActivePackage,
  EntityType,
  LinkedEntity,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { EmptyState } from '../ConferencePages/ui/EmptyState';
import { ActivePackageStatusBadge } from './ActivePackageStatusBadge';
import { AncestryBadge } from './AncestryBadge';
import { PostConferenceMergeStatusBadge } from './PostConferenceMergeStatusBadge';
import { AskFusionAssistantButton } from '../fusionAssistant/AskFusionAssistantButton';
import { FusionAssistantContext } from '../fusionAssistant/seedPrompt';

interface ActivePackagesListTableProps {
  data: ActivePackage[];
  loading: boolean;
  onRefresh: () => void;
  // Free-text description of the current scope (e.g. conference/venue) for the header.
  scopeDescription?: string;
  // Display-only: maps (conference, venue) to the coordinator's canonicalVenueName, falling back to
  // the raw venue. Wires the Venue column + text filter to the same canonical name the sibling
  // Launches/Groups tables show (tcx-core-platforms-903 D2 / finding on CR-301380769).
  venueDisplayName?: (conference?: string, venue?: string) => string;
  // When true, show the admin-only "Regenerate table" action (RefreshAllActivePackages).
  isAdmin?: boolean;
  onRegenerate?: () => void;
  regenerating?: boolean;
  onRecomputeDiffs?: () => void;
  recomputingDiffs?: boolean;
}

const COLUMN_MIN_WIDTH = 160;
const DASH = '-';

// Columns shown by default: everything except Major version. All columns are
// user-toggleable via CollectionPreferences, and the chosen set + page size are
// persisted to localStorage (like the other tables) so they survive a refresh.
const DEFAULT_VISIBLE_COLUMNS = [
  'packageName',
  'status',
  'conference',
  'venue',
  'linkedEntities',
  'tlcBranch',
  'tlcCommit',
  'packageType',
  'publishingBranch',
  'postConferenceFLCMergeStatus',
  'lastUpdated',
  'publicBranch',
  'publicCommit',
  'ancestryStatus',
  'diffLink',
  'lastDiffCheckedAt',
  'fusionAssistant',
];

const ACTIVE_PACKAGES_COLUMNS = 'activePackagesColumns';
const ACTIVE_PACKAGES_PAGE_SIZE = 'activePackagesPageSize';
// One-time marker so the fusionAssistant default-on backfill runs exactly once
// per browser. After it runs, an absent column means the user hid it, not that
// their preference predates the column — so we must stop re-adding it.
const ACTIVE_PACKAGES_FUSION_ASSISTANT_MIGRATED =
  'activePackagesFusionAssistantMigrated';

const shortCommit = (commit?: string): string =>
  commit ? commit.substring(0, 7) : '';

const truncate = (value: string, max = 50): string =>
  value.length > max ? `${value.substring(0, max)}…` : value;

const formatDate = (date?: Date): string =>
  date ? new Date(date).toLocaleString() : DASH;

// A linked task/group rendered as a link to its detail page, with its short commit.
const LinkedEntityLink = ({ entity }: { entity: LinkedEntity }) => {
  const href =
    entity.entityType === EntityType.GROUP
      ? `#/groups/view-${entity.entityId}`
      : `#/conferences/view-${entity.entityId}`;
  const label = truncate(entity.entityName || entity.entityId);
  const commit = shortCommit(entity.commitId);
  return (
    <Box>
      <Link href={href}>{label}</Link>
      {commit && (
        <span style={{ fontFamily: 'monospace', color: '#5f6b7a' }}>
          {' '}
          ({commit})
        </span>
      )}
    </Box>
  );
};

const LinkedEntitiesCell = ({ pkg }: { pkg: ActivePackage }) => {
  const entities = pkg.linkedEntities ?? [];
  if (entities.length === 0) {
    return <>{DASH}</>;
  }
  return (
    <SpaceBetween size='xxs'>
      {entities.map((e) => (
        <LinkedEntityLink key={`${e.entityType}#${e.entityId}`} entity={e} />
      ))}
    </SpaceBetween>
  );
};

// A row knows its package outright, but its task/group identity only through
// the entities that reference it. The first linked entity is the one the row
// most closely belongs to, so it becomes the subject of the assistant prompt.
const buildRowContext = (pkg: ActivePackage): FusionAssistantContext => {
  const context: FusionAssistantContext = {
    packages: [{ packageName: pkg.packageName ?? '', commitId: pkg.tlcCommit }],
  };
  const primary = (pkg.linkedEntities ?? [])[0];
  if (primary) {
    if (primary.entityType === EntityType.GROUP) {
      context.groupId = primary.entityId;
      context.groupName = primary.entityName;
    } else {
      context.launchId = primary.entityId;
      context.taskName = primary.entityName;
    }
  }
  return context;
};

export const ActivePackagesListTable = ({
  data,
  loading,
  onRefresh,
  scopeDescription,
  venueDisplayName,
  isAdmin,
  onRegenerate,
  regenerating,
  onRecomputeDiffs,
  recomputingDiffs,
}: ActivePackagesListTableProps) => {
  const [preferences, setPreferences] = useState<{
    pageSize: number;
    visibleContent: string[];
  }>({
    pageSize: 20,
    visibleContent: DEFAULT_VISIBLE_COLUMNS,
  });

  // Load saved column/page-size preferences from localStorage on mount.
  useEffect(() => {
    const alreadyMigrated = !!localStorage.getItem(
      ACTIVE_PACKAGES_FUSION_ASSISTANT_MIGRATED
    );
    // Record the migration on the very first mount, unconditionally — even for
    // a user with nothing stored yet (fresh, or page-size-only). Their first
    // save persists the default set (which includes fusionAssistant); if the
    // marker weren't set here, a later deliberate hide would save a set without
    // the column while the marker was still absent, and the next load would
    // treat that as a pre-column preference and re-add it, silently undoing the
    // hide once. Writing it here closes that gap. Do this in the effect body,
    // not inside the setPreferences updater: the write is a side effect, and
    // React can invoke a state updater more than once (StrictMode / purity
    // checks).
    if (!alreadyMigrated) {
      localStorage.setItem(ACTIVE_PACKAGES_FUSION_ASSISTANT_MIGRATED, '1');
    }

    const storedColumns = localStorage.getItem(ACTIVE_PACKAGES_COLUMNS);
    const storedPageSize = localStorage.getItem(ACTIVE_PACKAGES_PAGE_SIZE);
    if (!storedColumns && !storedPageSize) {
      return;
    }

    let visibleContent: string[] | undefined;
    if (storedColumns) {
      const stored = storedColumns.split(',');
      // A preference saved before this column existed won't list it, which
      // would hide it for returning users. Show it by default for them — but
      // only on the pre-migration load (gated on the value captured above), so
      // a deliberate hide afterwards sticks. An old preference and a hidden
      // column are indistinguishable here (both are just absent from the stored
      // set), so this backfill runs exactly once.
      if (!alreadyMigrated && !stored.includes('fusionAssistant')) {
        stored.push('fusionAssistant');
      }
      visibleContent = stored;
    }

    setPreferences((prev) => ({
      ...prev,
      ...(visibleContent && { visibleContent }),
      ...(storedPageSize && { pageSize: parseInt(storedPageSize, 10) }),
    }));
  }, []);

  // Persist column preferences to localStorage.
  useEffect(() => {
    localStorage.setItem(
      ACTIVE_PACKAGES_COLUMNS,
      preferences.visibleContent?.toString() ?? ''
    );
  }, [preferences.visibleContent]);

  // Persist page size to localStorage.
  useEffect(() => {
    localStorage.setItem(
      ACTIVE_PACKAGES_PAGE_SIZE,
      preferences.pageSize?.toString() ?? ''
    );
  }, [preferences.pageSize]);

  const columns = [
    {
      id: 'packageName',
      header: 'Package',
      cell: (p: ActivePackage) => (
        <span style={{ fontFamily: 'monospace' }}>{p.packageName}</span>
      ),
      sortingField: 'packageName',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (p: ActivePackage) =>
        p.status ? <ActivePackageStatusBadge status={p.status} /> : DASH,
      sortingField: 'status',
      minWidth: 120,
    },
    {
      id: 'conference',
      header: 'Conference',
      cell: (p: ActivePackage) => p.conference || DASH,
      sortingField: 'conference',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'venue',
      header: 'Venue',
      // Show the coordinator's canonical name when set, else the raw venue (display-only; sorting/
      // filtering/identity stay on the raw venue), matching the Launches/Groups tables. D2.
      cell: (p: ActivePackage) =>
        venueDisplayName
          ? venueDisplayName(p.conference, p.venue)
          : p.venue || DASH,
      sortingField: 'venue',
      minWidth: 120,
    },
    {
      id: 'linkedEntities',
      header: 'Linked task(s)/group(s)',
      cell: (p: ActivePackage) => <LinkedEntitiesCell pkg={p} />,
      sortingComparator: (a: ActivePackage, b: ActivePackage) =>
        (a.linkedEntities?.length ?? 0) - (b.linkedEntities?.length ?? 0),
      minWidth: 240,
    },
    {
      id: 'tlcBranch',
      header: 'FLC branch',
      cell: (p: ActivePackage) => p.tlcBranch || DASH,
      sortingField: 'tlcBranch',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'tlcCommit',
      header: 'FLC commit',
      cell: (p: ActivePackage) =>
        p.tlcCommit ? (
          <span style={{ fontFamily: 'monospace' }}>
            {shortCommit(p.tlcCommit)}
          </span>
        ) : (
          DASH
        ),
      sortingField: 'tlcCommit',
      minWidth: 120,
    },
    {
      id: 'packageType',
      header: 'Package type',
      cell: (p: ActivePackage) => p.packageType || DASH,
      sortingField: 'packageType',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'majorVersion',
      header: 'Major version',
      cell: (p: ActivePackage) => p.majorVersion || DASH,
      sortingField: 'majorVersion',
      minWidth: 120,
    },
    {
      id: 'publishingBranch',
      header: 'Post-launch FLC merge destination branch',
      cell: (p: ActivePackage) => p.publishingBranch || DASH,
      sortingField: 'publishingBranch',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'postConferenceFLCMergeStatus',
      header: 'Post-launch FLC merge status',
      cell: (p: ActivePackage) => (
        <PostConferenceMergeStatusBadge
          status={p.postConferenceFLCMergeStatus}
        />
      ),
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'lastUpdated',
      header: 'Last updated',
      cell: (p: ActivePackage) => formatDate(p.lastUpdated),
      sortingComparator: (a: ActivePackage, b: ActivePackage) =>
        (a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0) -
        (b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0),
      minWidth: COLUMN_MIN_WIDTH,
    },
    // --- Placeholder diff columns (populated later by 594 / 801) ---
    {
      id: 'publicBranch',
      header: 'Public branch',
      cell: (p: ActivePackage) => p.publicBranch || DASH,
      sortingField: 'publicBranch',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'publicCommit',
      header: 'Public commit',
      cell: (p: ActivePackage) =>
        p.publicCommit ? (
          <span style={{ fontFamily: 'monospace' }}>
            {shortCommit(p.publicCommit)}
          </span>
        ) : (
          DASH
        ),
      sortingField: 'publicCommit',
      minWidth: 120,
    },
    {
      id: 'ancestryStatus',
      header: 'Ancestry',
      cell: (p: ActivePackage) => <AncestryBadge status={p.ancestryStatus} />,
      sortingField: 'ancestryStatus',
      minWidth: 140,
    },
    {
      id: 'diffLink',
      header: 'Diff',
      cell: (p: ActivePackage) =>
        p.diffLink ? (
          <Link href={p.diffLink} external>
            View diff
          </Link>
        ) : (
          DASH
        ),
      minWidth: 120,
    },
    {
      id: 'lastDiffCheckedAt',
      header: 'Diff checked',
      cell: (p: ActivePackage) => formatDate(p.lastDiffCheckedAt),
      sortingComparator: (a: ActivePackage, b: ActivePackage) =>
        (a.lastDiffCheckedAt ? new Date(a.lastDiffCheckedAt).getTime() : 0) -
        (b.lastDiffCheckedAt ? new Date(b.lastDiffCheckedAt).getTime() : 0),
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'fusionAssistant',
      header: 'Fusion Assistant',
      cell: (p: ActivePackage) => (
        <AskFusionAssistantButton
          variant='inline-link'
          label='Ask'
          context={buildRowContext(p)}
          dataTestId='ask-fusion-assistant-package-button'
        />
      ),
      minWidth: 120,
    },
  ];

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(data || [], {
    filtering: {
      empty: (
        <EmptyState
          title='No active packages'
          subtitle='No packages are currently entered for the selected scope.'
          action={<></>}
        />
      ),
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='No active packages matched the specified filter'
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Clear filter
            </Button>
          }
        />
      ),
      filteringFunction: (pkg, filteringText) => {
        if (!pkg) return false;
        const text = filteringText.toLowerCase();
        const linkedMatch = (pkg.linkedEntities ?? []).some(
          (e) =>
            (e.entityName?.toLowerCase().includes(text) ?? false) ||
            (e.entityId?.toLowerCase().includes(text) ?? false) ||
            (e.commitId?.toLowerCase().includes(text) ?? false)
        );
        // Also match the coordinator's canonical venue display name: the Venue column renders
        // venueDisplayName, so filtering only on the raw venue would miss a canonical-name query
        // (tcx-core-platforms-903 D2 / finding on CR-301380769).
        const displayVenue = venueDisplayName
          ? venueDisplayName(pkg.conference, pkg.venue).toLowerCase()
          : '';
        return (
          (pkg.packageName?.toLowerCase().includes(text) ?? false) ||
          (pkg.status?.toLowerCase().includes(text) ?? false) ||
          (pkg.conference?.toLowerCase().includes(text) ?? false) ||
          (pkg.venue?.toLowerCase().includes(text) ?? false) ||
          displayVenue.includes(text) ||
          (pkg.tlcBranch?.toLowerCase().includes(text) ?? false) ||
          (pkg.tlcCommit?.toLowerCase().includes(text) ?? false) ||
          (pkg.packageType?.toLowerCase().includes(text) ?? false) ||
          linkedMatch
        );
      },
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: columns[0],
        isDescending: false,
      },
    },
  });

  return (
    <Box margin={{ top: 'l', bottom: 'xxl' }}>
      <Table
        {...collectionProps}
        resizableColumns
        wrapLines
        columnDefinitions={columns}
        visibleColumns={preferences.visibleContent}
        items={items}
        loadingText='Retrieving active packages'
        loading={loading}
        trackBy={(p: ActivePackage) =>
          `${p.packageName}#${p.conference}#${p.venue}`
        }
        stickyHeader={true}
        header={
          <Header
            description={scopeDescription}
            actions={
              <SpaceBetween direction='horizontal' size='xs'>
                {isAdmin && onRecomputeDiffs && (
                  <Button
                    onClick={onRecomputeDiffs}
                    loading={recomputingDiffs}
                    data-test-id='recompute-diffs-active-packages-button'
                  >
                    Recompute diffs
                  </Button>
                )}
                {isAdmin && onRegenerate && (
                  <Button
                    onClick={onRegenerate}
                    loading={regenerating}
                    data-test-id='regenerate-active-packages-button'
                  >
                    Regenerate table
                  </Button>
                )}
                <Button
                  iconName='refresh'
                  onClick={onRefresh}
                  data-test-id='refresh-active-packages-button'
                >
                  Refresh
                </Button>
              </SpaceBetween>
            }
            counter={
              data && filteredItemsCount !== undefined
                ? `(${filteredItemsCount}/${data.length})`
                : `(${items.length})`
            }
          >
            Active packages
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Filter by package, status, venue, branch, commit, or linked task/group'
          />
        }
        preferences={
          <CollectionPreferences
            onConfirm={({ detail }) =>
              setPreferences({
                pageSize: detail.pageSize ?? 20,
                visibleContent: detail.visibleContent as string[],
              })
            }
            preferences={preferences}
            title='Preferences'
            confirmLabel='Confirm'
            cancelLabel='Cancel'
            visibleContentPreference={{
              title: 'Select visible columns',
              options: [
                {
                  label: 'Columns',
                  options: [
                    { id: 'packageName', label: 'Package' },
                    { id: 'status', label: 'Status' },
                    { id: 'conference', label: 'Conference' },
                    { id: 'venue', label: 'Venue' },
                    { id: 'linkedEntities', label: 'Linked task(s)/group(s)' },
                    { id: 'tlcBranch', label: 'FLC branch' },
                    { id: 'tlcCommit', label: 'FLC commit' },
                    { id: 'packageType', label: 'Package type' },
                    { id: 'majorVersion', label: 'Major version' },
                    {
                      id: 'publishingBranch',
                      label: 'Post-launch FLC merge destination branch',
                    },
                    {
                      id: 'postConferenceFLCMergeStatus',
                      label: 'Post-launch FLC merge status',
                    },
                    { id: 'lastUpdated', label: 'Last updated' },
                    { id: 'publicBranch', label: 'Public branch' },
                    { id: 'publicCommit', label: 'Public commit' },
                    { id: 'ancestryStatus', label: 'Ancestry' },
                    { id: 'diffLink', label: 'Diff' },
                    { id: 'lastDiffCheckedAt', label: 'Diff checked' },
                    { id: 'fusionAssistant', label: 'Fusion Assistant' },
                  ],
                },
              ],
            }}
            pageSizePreference={{
              title: 'Select page size',
              options: [
                { value: 20, label: '20 packages' },
                { value: 50, label: '50 packages' },
                { value: 100, label: '100 packages' },
              ],
            }}
          />
        }
      />
    </Box>
  );
};

export default ActivePackagesListTable;
